/**
 * The commander's diorama view, drawn (L1, read-only): over the overview camera, a 2D layer with the team's squads as
 * markers in X23's grammar (a team-coloured block with how many stand, the order in words under it, a dashed path to
 * what the order is about) and the enemy as the team knows it (the track markers the HUD already uses, fading with
 * confidence), plus a card for the squad clicked (its order, each member's state). Reads `core/commandTable.ts`;
 * writes nothing to the match. The Lab's Commander view row turns the O key on (`setCommandViewEnabled`).
 */
import * as THREE from 'three';
import type { GameState, PlanetState, Vec3, VehicleKind } from '../types';
import { getSurfaceRadius } from '../core/surface';
import { personSetOf } from '../core/scaleProfile';
import { vecAdd, vecNormalize, vecScale } from '../math';
import type { CommandTable, FriendlyStatus, FrontView, SquadMarker } from '../core/commandTable';
import { drawTrackMarker, type TrackMarkerKind } from './trackMarker';
import { TEAMS } from '../constants';
import { dropshipMarks, dropshipWords } from '../ui/dropshipMarks';
import { drawBuildingMarker, drawVehiclePlate, VEHICLE_PLATE, VEHICLE_GLYPH, drawEnemyMark, drawLink, drawPlayLink, playLineHtml, drawIcon, drawMarker, drawIntentTag, drawRoleMark, ICON_SIZES, ORDER_ICON, type EnemyKind, type IconId } from '../ui/commandIcons';
import { ORDER_GLYPHS, buildingMarkers, mergeScreenBlocks } from '../core/commandTable';
import { observeIntent, type SquadIntent } from '../core/squadIntent';
import { mountedStructure } from '../core/seats';
import { placeCard, respawnPlaces, type RespawnPick } from '../core/respawnPick';
import { baseSupplyCard, fobSupplyCard, tableTrucks, truckCard, type TruckCard } from '../core/supplyCards';
import { drives } from '../core/body';
import type { Belief } from '../core/picture';
import type { Coverage } from '../ui/radarCoverage';
import { drawRadarCoverage, radarDrawStats } from './radarCoverageDraw';

/** 68.7 + lane E's icons: a friendly's state as its marker icon (free reads as holding; down as hurt, in grey). */
const STATUS_ICON: Record<FriendlyStatus, IconId> = {
  'in contact': 'contact', moving: 'moving', holding: 'holding', scouting: 'scouting', crewing: 'crewing',
  withdrawing: 'withdrawing', hurt: 'hurt', down: 'hurt', free: 'holding', supporting: 'supporting',   // 68.22
};

/**
 * 68.24 (the owner: "some indicator to be placed where I indicate for action to happen on the action wheel, and that
 * indicator lasts as long as the order is in effect / being followed"): a squad order's mark at its place — a ring on the
 * ground and the squad's plate above it with the order's icon, pinned to the ring. Drawn from the table, whose target is
 * the order in force (core/commandTable.ts, `squadOrderFor`): it lasts exactly as long as the order does, in both views.
 */
function orderMark(g: CanvasRenderingContext2D, x: number, y: number, glyph: string, color: string, alpha: number): void {
  const size = ORDER_MARK;
  g.save();
  g.globalAlpha = alpha;
  g.lineWidth = 2; g.strokeStyle = '#0e1018';
  g.beginPath(); g.ellipse(x, y, 9, 4.5, 0, 0, Math.PI * 2); g.stroke();
  g.lineWidth = 1.5; g.strokeStyle = color;
  g.beginPath(); g.ellipse(x, y, 9, 4.5, 0, 0, Math.PI * 2); g.stroke();
  g.beginPath(); g.moveTo(x, y); g.lineTo(x, y - 8); g.stroke();
  drawMarker(g, orderIconOf(glyph), x - size / 2, y - 8 - size, size, color);
  g.restore();
}

/** An order's glyph (core's ORDER_GLYPHS) to its icon (ORDER_ICON). */
function orderIconOf(glyph: string): IconId {
  const key = (Object.keys(ORDER_GLYPHS) as Array<keyof typeof ORDER_GLYPHS>).find(k => ORDER_GLYPHS[k] === glyph) ?? 'fire';
  return ORDER_ICON[key] as IconId;
}

let friendlyOverride: boolean | null = null;
/** 68.7: the setting (null = on: decision 147 retired the Lab's row and `?friendlies`). The K key flips it for the session. */
/** 63.1s: each vehicle kind's name on its plate. */
const VEHICLE_NAME: Record<VehicleKind, string> = { wheeled: 'transport', technical: 'technical', supplyTruck: 'supply truck', motorcycle: 'bike', tracked: 'tracked carrier', tank: 'tank' };   // (197 step 1: the M113)

export function setFriendlyMarkers(v: boolean | null): void { friendlyOverride = v; }
export function friendlyMarkersOn(): boolean { return friendlyOverride ?? true; }

/** 68.7: a state's colour, one per state, the same everywhere a friendly is drawn. */
export const STATUS_COLORS: Record<FriendlyStatus, string> = {
  'in contact': '#ff6a4a', hurt: '#e0b341', down: '#7a7f8e', crewing: '#5fd0e0', scouting: '#b48cff',
  withdrawing: '#d07ae0', holding: '#6fd08c', moving: '#e8eaf0', free: '#c8ccd8', supporting: '#ffd27a',   // 68.22: a play's base of fire
};
/** Within this many person-heights a squad opens into its members. */
export const OPEN_HEIGHTS = 30;

let enabledOverride: boolean | null = null;
/** The commander's view (null = on: decision 147 retired the Lab's row and `?command`; the setter is kept for a game or design toggle later). */
export function setCommandViewEnabled(v: boolean | null): void { enabledOverride = v; }
export function commandViewEnabled(): boolean { return enabledOverride ?? true; }

const ENEMY = '#e05a4f';
/** 68.28: the held unit's ring at the table, px (radius). */
const YOU_RING = 9;
/** 68.24: an order mark's plate, px. */
const ORDER_MARK = 20;
/** 68.20: a unit's dot at the table, px (radius). */
const UNIT_DOT = 3;
/** 201 (8): a medic's or an engineer's badge in a unit's dot's place (px; ui/commandIcons.ts drawRoleMark). */
const ROLE_BADGE = 11;

/**
 * 68.14 follow-up: what an enemy mark shows the unit as — a radar dot (no identity) `unknown`; an identified unit seen now
 * by what it is crewing (a vehicle that drives, a gun that does not), else on foot; a stale track as last seen on foot
 * (where it is now, and in what, is not known — the mark never leaks it).
 */
function enemyKind(state: GameState, b: Belief, seenNow: boolean): EnemyKind {
  if (b.unitId === undefined) return 'unknown';
  if (!seenNow) return 'infantry';
  const u = state.players.find(p => p.id === b.unitId);
  const body = u?.mount ? mountedStructure(u, state) : null;
  if (!body) return 'infantry';
  return drives(body) ? 'vehicle' : 'gun';
}
/** 69.6d: the front's colour (the active sector's edges and name). */
const FRONT = '#f0c060';

/**
 * 68.7: where a point on a planet's surface (lifted by `lift`) lands on a `w`×`h` screen: in front of the camera, its
 * projection; behind, mirrored onto a row above the HUD's lower text; over the planet's limb, null. Clamped on screen.
 */
export function screenPlace(state: Pick<GameState, 'planets'>, camera: THREE.Camera, planetIndex: number, dir: Vec3, lift: number, w: number, h: number, v = new THREE.Vector3()): { x: number; y: number; behind: boolean; dist: number } | null {
  const p = state.planets[planetIndex];
  if (!p) return null;
  const cam = camera.position;
  const r = getSurfaceRadius(p as PlanetState, dir) + lift;   // the ground under it (the terrain can sit ten people off the nominal radius)
  const world = { x: p.position.x + dir.x * r, y: p.position.y + dir.y * r, z: p.position.z + dir.z * r };
  const dist = Math.hypot(world.x - cam.x, world.y - cam.y, world.z - cam.z);
  const toCam = { x: cam.x - world.x, y: cam.y - world.y, z: cam.z - world.z };
  if (toCam.x * dir.x + toCam.y * dir.y + toCam.z * dir.z < -0.2 * dist) return null;   // over the limb: the planet is between
  v.set(world.x, world.y, world.z).applyMatrix4(camera.matrixWorldInverse);
  const behind = v.z > 0;
  v.set(world.x, world.y, world.z).project(camera);
  let x = (v.x + 1) / 2 * w, y = (1 - v.y) / 2 * h;
  if (behind) { x = w - x; y = h - 84; }
  return { x: Math.max(60, Math.min(w - 60, x)), y: Math.max(90, Math.min(h - 84, y)), behind, dist };
}

export class CommandOverlay {
  private canvas: HTMLCanvasElement;
  private card: HTMLDivElement;
  private legend: HTMLDivElement;
  private markerCache = new Map<string, HTMLCanvasElement>();
  private v = new THREE.Vector3();
  /** 68.4: where each squad's block was drawn last frame (after stacking) — what a click or a box picks by. */
  readonly drawnAt = new Map<number, { x: number; y: number }>();
  /** 68.35: each squad's merged group as last drawn (its squads' ids; one alone when not merged) — a click on a merged block selects them all. */
  readonly groupOf = new Map<number, number[]>();
  /** 68.18: where each transport's plate was drawn (the host picks one by click). */
  readonly transportsAt = new Map<number, { x: number; y: number }>();
  /** 68.33: where each of the team's places (its base, relays, FOBs) is on screen (the host picks one by click). */
  readonly placesAt = new Map<string, { at: RespawnPick; x: number; y: number }>();
  /** 166: where each of the team's supply trucks is on screen (the host opens its card by click, as a place's). */
  readonly trucksAt = new Map<number, { x: number; y: number }>();
  /** 68.43: the side's radar coverage to draw under the marks (the host's ui/radarCoverage.ts grids; none without the Lab row). */
  radar: readonly Coverage[] = [];
  /** 68.43: the radars whose names the overlay writes at their heads (the reach's grids: every radar the side may draw). */
  radarHeads: readonly Coverage[] = [];
  /** 68.43: the host's radar upkeep this frame (ms), written to the canvas's dataset with the labels' cost for the readout. */
  radarCostMs = 0;
  /** 68.43: with the detail asked for and nothing drawn, why (ui/radarCoverage.ts radarNote) — a line at the screen's foot. */
  radarNote: string | null = null;
  /** 68.33: the card's "Respawn here" — the host issues the pick. */
  onRespawnHere: ((at: RespawnPick) => void) | null = null;
  private cardHtml = '';
  /** 166: the card's supply lines, set apart from its button (they change by the second; the button must not). */
  private cardSupply = '';

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = 'position:fixed; inset:0; width:100vw; height:100vh; pointer-events:none; z-index:20;';
    this.card = document.createElement('div');
    this.card.style.cssText = 'position:fixed; left:16px; top:64px; width:260px; background:rgba(12,12,24,0.92); border:1px solid rgba(255,255,255,0.15); border-radius:8px; padding:10px 12px; color:#e8eaf0; font:12px system-ui, sans-serif; z-index:21; display:none;';
    this.legend = document.createElement('div');
    this.legend.style.cssText = 'position:fixed; left:50%; top:92px; transform:translateX(-50%); background:rgba(12,12,24,0.8); border-radius:6px; padding:5px 12px; color:#c8ccd8; font:12px system-ui, sans-serif; z-index:21; display:none;';
    document.body.append(this.canvas, this.card, this.legend);
  }

  dispose(): void { this.canvas.remove(); this.card.remove(); this.legend.remove(); }

  hide(): void { this.canvas.style.display = 'none'; this.card.style.display = 'none'; this.legend.style.display = 'none'; }

  /** Where a squad marker sits on screen (null off-screen or behind the planet's limb). */
  project(state: GameState, camera: THREE.Camera, planetIndex: number, dir: Vec3, lift = 0): { x: number; y: number } | null {
    const p = state.planets[planetIndex];
    if (!p) return null;
    const r = getSurfaceRadius(p, dir) + lift;
    this.v.set(p.position.x + dir.x * r, p.position.y + dir.y * r, p.position.z + dir.z * r);
    // behind the planet: the surface normal faces away from the camera
    const cam = camera.position;
    const toCam = { x: cam.x - this.v.x, y: cam.y - this.v.y, z: cam.z - this.v.z };
    if (toCam.x * dir.x + toCam.y * dir.y + toCam.z * dir.z < 0) return null;
    this.v.project(camera);
    if (this.v.z > 1) return null;
    return { x: (this.v.x + 1) / 2 * window.innerWidth, y: (1 - this.v.y) / 2 * window.innerHeight };
  }

  /**
   * 68.7: the friendlies in the L0 view — a box per squad far off (its glyph, how many stand, what it is doing), opening
   * into a box per member within OPEN_HEIGHTS; behind the camera, faded at the screen's bottom edge; never within reach
   * of the crosshair at the screen's centre. `viewerId` is the held unit (never boxed).
   */
  drawFriendlies(state: GameState, camera: THREE.Camera, table: CommandTable, viewerId: number | null, person: number, lift: number): void {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth, h = window.innerHeight;
    if (this.canvas.width !== Math.round(w * dpr) || this.canvas.height !== Math.round(h * dpr)) { this.canvas.width = Math.round(w * dpr); this.canvas.height = Math.round(h * dpr); }
    this.canvas.style.display = ''; this.card.style.display = 'none'; this.legend.style.display = 'none';
    const g = this.canvas.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    const cx = w / 2, cy = h / 2, clear = 56;
    const cam = camera.position;
    const place = (planetIndex: number, dir: Vec3) => screenPlace(state, camera, planetIndex, dir, lift, w, h, this.v);
    const boarded = new Set<number>();   // 208: those seated in the wave ship's container (alive, not seen): no marker of their own
    for (const p of state.players) if (p.boarded) boarded.add(p.id);
    const box = (x: number, y: number, count: number | null, status: FriendlyStatus, behind: boolean, small: boolean) => {
      if (Math.hypot(x - cx, y - cy) < clear) return;   // never over the crosshair
      const col = STATUS_COLORS[status];
      const team = status === 'down' ? '#5a5f6e' : TEAMS[table.squads[0]?.team % TEAMS.length]?.primary ?? '#6e9ec2';
      const size = small ? 18 : ICON_SIZES.markerFrame;
      g.save();
      g.globalAlpha = behind ? 0.35 : 0.92;
      drawMarker(g, STATUS_ICON[status], x - size / 2, y - size / 2, size, team);   // lane E's frame and state glyph (X23)
      if (!small) {
        g.font = '600 11px system-ui, sans-serif'; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillStyle = '#ffffff';
        if (count !== null) g.fillText(String(count), x + size / 2 + 3, y);
        g.font = '500 10px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'top'; g.fillStyle = col;
        g.fillText(status, x, y + size / 2 + 2);
      }
      g.restore();
    };
    // 68.24: each squad's order where it was pointed, while it is in force (the same mark as the table's)
    for (const m of table.squads) {
      if (!m.target || !m.alive) continue;
      const t = place(m.target.planetIndex, m.target.dir);
      if (!t || Math.hypot(t.x - cx, t.y - cy) < clear) continue;
      orderMark(g, t.x, t.y, m.glyph, TEAMS[m.team % TEAMS.length].primary, t.behind ? 0.35 : 0.92);
    }
    // 68.29 (the owner: "I'd like to add those little blue dots on friendlies on ground view"): 68.20's dot under each
    // living friendly on foot (never the one you hold), fading with distance, under the boxes and the order marks
    for (const m of table.squads) {
      const col = TEAMS[m.team % TEAMS.length].primary;
      for (const u of m.members) {
        if (!u.alive || u.mounted || u.id === viewerId || boarded.has(u.id)) continue;
        const p = place(u.planetIndex, u.dir);
        if (!p || p.behind) continue;
        g.save();
        g.globalAlpha = Math.max(0.35, Math.min(0.9, 1 - p.dist / (OPEN_HEIGHTS * person * 8)));
        if (u.role) drawRoleMark(g, u.role, p.x, p.y, ROLE_BADGE - 1);   // 201 (8): a medic's or an engineer's badge in the dot's place
        else { g.beginPath(); g.arc(p.x, p.y, UNIT_DOT - 0.5, 0, Math.PI * 2); g.fillStyle = col; g.fill(); g.lineWidth = 1; g.strokeStyle = '#0e1018'; g.stroke(); }
        g.restore();
      }
    }
    // 68.30: a vehicle with the team's squads aboard is one small plate (lane E's) over it — the hull, the troops aboard,
    // a tab per squad — never over the crosshair; the squads aboard get no boxes of their own
    for (const tr of table.transports) {
      if (!tr.aboard) continue;
      const at = place(tr.planetIndex, tr.dir);
      if (!at || Math.hypot(at.x - cx, at.y - cy) < clear) continue;
      const lead = table.squads.find(m => m.id === tr.aboard!.lead)!;
      g.save(); g.globalAlpha = at.behind ? 0.35 : 0.92;
      const plateY = at.y - VEHICLE_PLATE.h * 0.35 - 8;
      drawVehiclePlate(g, at.x, plateY, { team: TEAMS[lead.team % TEAMS.length].primary, count: tr.aboard.count, squads: tr.aboard.squads.map(id => table.squads.find(m => m.id === id)!.label.split(' ')[0]), glyph: VEHICLE_GLYPH[tr.kind] }, 0.7);   // 63.1s: its kind's glyph
      // X40: the lead squad's intent beside the plate (lane D left it to X40)
      if (lead.intent && !at.behind) drawIntentTag(g, lead.intent.intent, at.x + VEHICLE_PLATE.w * 0.35 + 3, plateY - 6.5, TEAMS[lead.team % TEAMS.length].primary, { where: 'ground', sinceChange: this.intentAge(lead) });
      g.restore();
    }
    for (const m of table.squads) {
      if (!m.centre || !m.alive || m.aboard !== undefined) continue;
      const at = place(m.planetIndex, m.centre);
      if (!at) continue;
      // X40: what the squad is doing now, over it — its glyph, and its word while it barks (lane E's tag)
      if (m.intent && !at.behind && Math.hypot(at.x - cx, at.y - cy - 32) >= clear) {
        g.save(); g.globalAlpha = 0.92;
        drawIntentTag(g, m.intent.intent, at.x - 6.5, at.y - 38, TEAMS[m.team % TEAMS.length].primary, { where: 'ground', sinceChange: this.intentAge(m) });
        g.restore();
      }
      if (at.dist > OPEN_HEIGHTS * person) { box(at.x, at.y, m.alive, m.status, at.behind, false); continue; }
      for (const u of m.members) {
        if (!u.alive || u.id === viewerId || u.mounted || boarded.has(u.id)) continue;   // 68.30: a member in a hull or a seat: the body stands for it (208: or in the wave ship's container)
        const p = place(u.planetIndex, u.dir);
        if (!p) continue;
        box(p.x, p.y, null, u.status, p.behind, true);
        // 68.5: a member on its own order wears that order's glyph beside its box
        if (u.own && Math.hypot(p.x - cx, p.y - cy) >= clear) {
          g.save(); g.globalAlpha = p.behind ? 0.35 : 0.92;
          g.font = '600 12px system-ui, sans-serif'; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillStyle = '#ffffff';
          g.fillText(u.own.glyph, p.x + 12, p.y);
          g.restore();
        }
      }
    }
  }

  /** 69.6d: the sectors and the front (core/commandTable.ts frontView). An edge is drawn where it is on screen and in front. */
  private front(g: CanvasRenderingContext2D, state: GameState, camera: THREE.Camera, fv: FrontView, team: number, alpha: number, lift: number): void {
    const own = TEAMS[team % TEAMS.length].primary;
    for (const e of fv.edges) {
      g.save();
      g.globalAlpha = alpha * (e.bold ? 0.95 : 0.5);
      g.strokeStyle = e.bold ? FRONT : '#c8ccd8';
      g.lineWidth = e.bold ? 3 : 1.5;
      if (!e.bold) g.setLineDash([8, 6]);
      g.beginPath();
      let drawing = false;
      for (const d of e.points) {
        const q = this.project(state, camera, fv.planetIndex, d, lift * 0.2);
        if (!q) { drawing = false; continue; }
        if (drawing) g.lineTo(q.x, q.y); else g.moveTo(q.x, q.y);
        drawing = true;
      }
      g.stroke();
      g.restore();
    }
    // the active sector's name first; another gives way where it would land on one already drawn (from orbit they bunch)
    const drawn: Array<{ x: number; y: number }> = [];
    for (const sec of [...fv.sectors].sort((u, v) => Number(v.status === 'active') - Number(u.status === 'active'))) {
      const q = this.project(state, camera, fv.planetIndex, sec.at, lift * 0.2);
      if (!q || q.y < 150 || q.y > window.innerHeight - 16) continue;
      if (drawn.some(d => Math.abs(d.x - q.x) < 110 && Math.abs(d.y - q.y) < 16)) continue;
      drawn.push(q);
      g.save();
      g.globalAlpha = alpha;
      g.font = sec.status === 'active' ? '700 12px system-ui, sans-serif' : '500 11px system-ui, sans-serif';
      g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillStyle = sec.status === 'active' ? FRONT : sec.status === 'taken' || sec.status === 'held' || sec.status === 'ours' ? own : '#9aa0ae';
      g.fillText(sec.label, q.x, q.y);
      g.restore();
    }
  }

  /** Draw the table; `alpha` fades it in with the camera move. */
  draw(state: GameState, camera: THREE.Camera, table: CommandTable, selected: ReadonlySet<number>, alpha: number, lift: number, drag: { x0: number; y0: number; x1: number; y1: number } | null = null, note = '', hover: { planetIndex: number; dir: Vec3; label: string } | null = null, held: number | null = null, place: RespawnPick | null = null, bare = false): void {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth, h = window.innerHeight;
    if (this.canvas.width !== Math.round(w * dpr) || this.canvas.height !== Math.round(h * dpr)) { this.canvas.width = Math.round(w * dpr); this.canvas.height = Math.round(h * dpr); }
    this.canvas.style.display = '';
    const g = this.canvas.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    g.globalAlpha = alpha;
    // 68.42: `bare` — the planet table draws the world's marks (its overview and orbit stops); the overlay keeps the unit you
    // hold, the hover ring, the reserve, the drag box and the card
    if (this.radarNote) {
      g.save();
      g.font = '500 12px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
      const tw = g.measureText(this.radarNote).width + 16;
      g.fillStyle = 'rgba(12,12,24,0.8)'; g.beginPath(); g.roundRect(w / 2 - tw / 2, h - 70, tw, 20, 5); g.fill();
      g.fillStyle = '#e8b04a'; g.fillText(this.radarNote, w / 2, h - 60);
      g.restore();
    }
    if (!bare) {
      // 68.43: the radars' coverage, on the ground under everything else
      if (this.radar.length) {
        const t0 = performance.now();
        drawRadarCoverage(g, state, camera, this.radar, alpha, lift * 0.2, w, h);
        this.canvas.dataset.radarMs = (performance.now() - t0).toFixed(2);   // (the frame budget's readout, 136)
        this.canvas.dataset.radarBuilds = String(radarDrawStats.builds);
      }
      // 68.43: each radar's name at its head (its line is in the scene: rendering/radarReachView.ts)
      const ht0 = performance.now();
      for (const c of this.radarHeads) {
        const q = this.project(state, camera, c.planetIndex, c.up, lift * 0.5);
        if (!q) continue;
        const colour = TEAMS[((c.team % TEAMS.length) + TEAMS.length) % TEAMS.length]?.primary ?? '#c8ccd8';
        g.save();
        g.globalAlpha = alpha * (c.own ? 0.9 : 0.75);
        g.font = '600 10px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'bottom';
        const text = `${c.label}${c.own ? '' : ' (seen)'}`, tw = g.measureText(text).width + 8;
        g.fillStyle = 'rgba(12,12,24,0.7)'; g.beginPath(); g.roundRect(q.x - tw / 2, q.y - 30, tw, 14, 3); g.fill();
        g.fillStyle = colour; g.fillText(text, q.x, q.y - 17.5);
        g.restore();
      }
      if (this.radarHeads.length) this.canvas.dataset.radarBasicMs = (this.radarCostMs + performance.now() - ht0).toFixed(3);
      // the enemy, as the team knows it
      for (const b of table.enemy) {
        const p = this.project(state, camera, b.planetIndex, b.surfaceDir, lift);
        if (!p) continue;
        // 68.14 follow-up (the owner: "a big green diamond over every enemy, then also a small red square … could be a tiny
        // icon"): lane E's enemy mark, alone at the table (the shoulder's markers stand down here) — seen now filled, known
        // and stale hollow; its kind from what the team knows; a mark of the team's own in the track grammar as before
        if (b.source === 'mark') { g.drawImage(this.marker('mark', b.confidence), p.x - 8, p.y - 8, 16, 16); continue; }
        const seen = b.source === 'seen' && b.confidence >= 0.9;
        g.save();
        g.globalAlpha = alpha * (seen ? 1 : Math.max(0.35, b.confidence));
        drawEnemyMark(g, enemyKind(state, b, seen), p.x, p.y, { seen });
        g.restore();
      }
      // 69.6d (decision 146): the front — each sector's edge a line across it, the active sector's two bold, each sector
      // named with how it stands for the team (taken / the front / ahead; lost / held for the defenders), under everything else
      if (table.front) this.front(g, state, camera, table.front, table.squads[0]?.team ?? 0, alpha, lift);
      // 69.2d: the web's links (lane E's drawLink), live from the HQ or dead (stranded), under the buildings
      for (const l of table.links) {
        const a = this.project(state, camera, l.planetIndex, l.a, lift * 0.5), b = this.project(state, camera, l.planetIndex, l.b, lift * 0.5);
        if (!a || !b) continue;
        g.save(); g.globalAlpha = alpha;
        drawLink(g, a.x, a.y, b.x, b.y, { live: l.live, team: TEAMS[(table.squads[0]?.team ?? 0) % TEAMS.length].primary });
        g.restore();
      }
      // 68.22: a play joins its support's position to its objective (lane E's drawPlayLink: dotted while the support sets,
      // the support's fire as dashes in the assault, a bar when lifted, named)
      for (const pl of table.plays) {
        if (!pl.objective || pl.phase === 'done') continue;
        const a = this.project(state, camera, pl.support.planetIndex, pl.support.dir, lift), b = this.project(state, camera, pl.objective.planetIndex, pl.objective.dir, lift);
        if (!a || !b) continue;
        g.save(); g.globalAlpha = alpha;
        drawPlayLink(g, a.x, a.y, b.x, b.y, { team: TEAMS[(table.squads[0]?.team ?? 0) % TEAMS.length].primary, phase: pl.phase, lifted: pl.lifted, label: `play ${pl.id + 1}` });
        g.restore();
      }
      // 63.7 (c): the buildings — the team's in its colour, the neutral sites in grey; a ruin dimmed — under the squads.
      // 68.14: a building's name gives way to the squads and to a name already drawn near it (the icon stays)
      const squadsAt = table.squads.filter(m => m.centre).map(m => this.project(state, camera, m.planetIndex, m.centre!, lift)).filter((q): q is { x: number; y: number } => !!q);
      const named: Array<{ x: number; y: number }> = [];
      for (const b of buildingMarkers(state, table.squads[0]?.team ?? 0)) {
        const p = this.project(state, camera, b.planetIndex, b.dir, lift * 0.5);
        if (!p || p.y < 150 || p.y > window.innerHeight - 16 || p.x < 16 || p.x > window.innerWidth - 16) continue;   // 68.14: not under the score bar, never pinned to an edge
        const crowded = squadsAt.some(q => Math.abs(q.x - p.x) < 70 && q.y - p.y > -40 && q.y - p.y < 120) || named.some(q => Math.abs(q.x - p.x) < 60 && Math.abs(q.y - p.y) < 30);
        // 68.14: lane E's plate (ui/commandIcons.ts drawBuildingMarker): a dark plate in the team's outline, its pin's tip on
        // the building; the neutral sites light grey, ruins dimmed (its own); the name under the pin
        const size = ICON_SIZES.markerFrame, k = size / 36;
        g.save();
        g.globalAlpha = alpha;
        drawBuildingMarker(g, b.kind as IconId, p.x - 18 * k, p.y - 41.5 * k, size, b.team === null ? null : TEAMS[b.team % TEAMS.length].primary, b.ruined);
        if (!crowded) {
          named.push(p);
          g.font = '500 9px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'top'; g.fillStyle = '#c8ccd8';
          g.fillText(b.ruined && b.kind !== 'ruin' ? `${b.label} (ruined)` : b.label, p.x, p.y + 3);
        }
        g.restore();
      }
      // 69.6h: your respawn pick — a tag over its place, green while it is honoured, amber while it is cut (the rule meanwhile)
      if (table.respawn) {
        const rp = table.respawn, p = this.project(state, camera, rp.planetIndex, rp.surfaceDir, lift * 0.5);
        if (p) {
          const text = rp.open ? 'respawn' : 'respawn \u00b7 cut', w = 8 + text.length * 5.6;
          g.save(); g.globalAlpha = alpha;
          g.font = '700 10px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
          g.fillStyle = 'rgba(12,12,24,0.8)'; g.beginPath(); g.roundRect(p.x - w / 2, p.y - 58, w, 15, 4); g.fill();
          g.strokeStyle = rp.open ? '#6fd08c' : '#e8b04a'; g.lineWidth = 1; g.stroke();
          g.fillStyle = rp.open ? '#bdf0cc' : '#f3d18b'; g.fillText(text, p.x, p.y - 50.5);
          g.restore();
        }
      }
      // 68.18: the transports — the team's and nobody's with their seats (filled / all), the enemy's where the picture puts a rider
      this.transportsAt.clear();
      this.drawnAt.clear();
      this.placesAt.clear();
      for (const pl of respawnPlaces(state, table.squads[0]?.team ?? 0)) {
        const q = this.project(state, camera, pl.planetIndex, pl.surfaceDir, lift * 0.5);
        if (q) this.placesAt.set(pl.kind === 'base' ? 'base' : String(pl.structureId), { at: pl.kind === 'base' ? 'base' : { structureId: pl.structureId! }, x: q.x, y: q.y });
      }
      const placed: Array<{ x: number; y: number }> = [];   // blocks and 68.30's plates, stacked apart (below)
      for (const tr of table.transports) {
        const p = this.project(state, camera, tr.planetIndex, tr.dir, lift * 0.5);
        if (!p || p.y < 150 || p.y > window.innerHeight - 16 || p.x < 16 || p.x > window.innerWidth - 16) continue;
        const size = ICON_SIZES.markerFrame, k = size / 36;
        // 68.30 (the owner: "squads in apcs will have squad boxes overshadow the vehicle icon … some combined thing"): with
        // the team's squads aboard, lane E's one plate — the hull, the troops aboard, a tab per squad — and the lead
        // squad's order line under it, in place of the transport's plate and their blocks; each tab picks its squad
        if (tr.aboard) {
          const lead = table.squads.find(m => m.id === tr.aboard!.lead)!, color = TEAMS[lead.team % TEAMS.length].primary;
          const py = p.y - VEHICLE_PLATE.h / 2 - 6;
          g.save(); g.globalAlpha = alpha;
          drawVehiclePlate(g, p.x, py, { team: color, count: tr.aboard.count, squads: tr.aboard.squads.map(id => table.squads.find(m => m.id === id)!.label.split(' ')[0]), selected: tr.aboard.squads.some(id => selected.has(id)), glyph: VEHICLE_GLYPH[tr.kind] });   // 63.1s: its kind's glyph
          // X40: the lead squad's intent beside the plate, as beside a block (lane D left it to X40)
          if (lead.intent) drawIntentTag(g, lead.intent.intent, p.x + VEHICLE_PLATE.w / 2 + 4, py - 8, color, { where: 'table', sinceChange: this.intentAge(lead) });
          g.restore();
          tr.aboard.squads.forEach((id, i) => this.drawnAt.set(id, { x: p.x - VEHICLE_PLATE.w / 2 + 3 + i * (VEHICLE_PLATE.tab.w + VEHICLE_PLATE.tab.gap) + VEHICLE_PLATE.tab.w / 2, y: py - VEHICLE_PLATE.h / 2 - VEHICLE_PLATE.tab.h / 2 + 2 }));
          if (lead.target) {
            const t = this.project(state, camera, lead.target.planetIndex, lead.target.dir, lift);
            if (t) {
              g.save(); g.globalAlpha = alpha; g.setLineDash([6, 5]); g.strokeStyle = color; g.lineWidth = 2; g.beginPath(); g.moveTo(p.x, py); g.lineTo(t.x, t.y); g.stroke(); g.restore();
              orderMark(g, t.x, t.y, lead.glyph, color, alpha);
            }
          }
          g.save(); g.globalAlpha = alpha; this.orderLine(g, p.x, py + VEHICLE_PLATE.h / 2 + 3, lead); g.restore();
          if (tr.rides) this.transportsAt.set(tr.id, { x: p.x, y: py });   // (63.1s: only a transport takes a ride order by click)
          placed.push({ x: p.x, y: py - 4 }, { x: p.x, y: py + 18 });   // the plate with its tabs and order line: blocks give way
          continue;
        }
        g.save(); g.globalAlpha = alpha * (tr.enemy ? 0.85 : 1);
        // 63.1s: every vehicle kind its own glyph and name (the transport's plate as before)
        drawBuildingMarker(g, VEHICLE_GLYPH[tr.kind], p.x - 18 * k, p.y - 41.5 * k, size, tr.team === null ? null : TEAMS[tr.team % TEAMS.length].primary);
        g.font = '600 9px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'top'; g.fillStyle = tr.enemy ? '#f0a0a0' : '#c8ccd8';
        g.fillText(tr.enemy ? `enemy ${VEHICLE_NAME[tr.kind]}` : `${VEHICLE_NAME[tr.kind]} ${tr.seats!.filled}/${tr.seats!.total}`, p.x, p.y + 3);
        g.restore();
        if (!tr.enemy && tr.rides) this.transportsAt.set(tr.id, p);
      }
      // DECISIONS 207 (8) (the owner: "a map indicator for the dropship wave vehicle like we do for other vehicles"): the side's
      // dropships — the wave ship and any called — on X23's plate as the vehicles', over the ground under them, in flight and
      // down; its words under it, and while it comes in a dashed line to where it lands
      for (const d of dropshipMarks(state, table.squads[0]?.team ?? 0)) {
        const p = this.project(state, camera, d.planetIndex, d.dir, lift * 0.5);
        if (!p || p.y < 150 || p.y > window.innerHeight - 16 || p.x < 16 || p.x > window.innerWidth - 16) continue;
        const size = ICON_SIZES.markerFrame, k = size / 36, color = TEAMS[d.team % TEAMS.length].primary;
        g.save(); g.globalAlpha = alpha;
        const to = d.to && this.project(state, camera, d.planetIndex, d.to, lift * 0.5);
        if (to) { g.setLineDash([4, 5]); g.strokeStyle = color; g.lineWidth = 1.5; g.beginPath(); g.moveTo(p.x, p.y); g.lineTo(to.x, to.y); g.stroke(); g.setLineDash([]); }
        drawBuildingMarker(g, 'dropship', p.x - 18 * k, p.y - 41.5 * k, size, color);
        g.font = '600 9px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'top'; g.fillStyle = '#c8ccd8';
        g.fillText(dropshipWords(d), p.x, p.y + 3);
        g.restore();
      }
      // 166: the team's supply trucks (cargo-only: no transport plate takes them) — a marker, its load and leg under it; a click opens its card
      this.trucksAt.clear();
      for (const tr of tableTrucks(state, table.squads[0]?.team ?? 0)) {
        const p = this.project(state, camera, tr.planetIndex, tr.surfaceDir, lift * 0.5), c = truckCard(state, table.squads[0]?.team ?? 0, tr.id);
        if (!p || !c || p.y < 150 || p.y > window.innerHeight - 16 || p.x < 16 || p.x > window.innerWidth - 16) continue;
        const size = ICON_SIZES.markerFrame, k = size / 36;
        g.save(); g.globalAlpha = alpha;
        drawBuildingMarker(g, 'crate' as IconId, p.x - 18 * k, p.y - 41.5 * k, size, TEAMS[(table.squads[0]?.team ?? 0) % TEAMS.length].primary);
        g.font = '600 9px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'top'; g.fillStyle = '#c8ccd8';
        g.fillText(`supply ${c.full ? 'loaded' : c.cargo.bandages + c.cargo.magazines > 0 ? 'part' : 'empty'} · ${truckLegWords(c)}`, p.x, p.y + 3);
        g.restore();
        this.trucksAt.set(tr.id, p);
        placed.push({ x: p.x, y: p.y - 18 }, { x: p.x, y: p.y + 8 });   // (its marker and its label: the squads' blocks give way, as to a plate)
      }
      // 68.20 (the owner: "a little blue dot for the individual units, not just the box indicating the squad, but boxes float
      // above dots like they do currently"): a dot in the team's colour on the ground under each living member on foot, under
      // the squads' blocks; never for the fallen, nor for a member in a hull or a seat (the body's own mark stands for it)
      for (const m of table.squads) {
        const color = TEAMS[m.team % TEAMS.length].primary;
        for (const u of m.members) {
          if (!u.alive || u.mounted) continue;
          const p = this.project(state, camera, u.planetIndex, u.dir, 0);
          if (!p) continue;
          g.save();
          g.globalAlpha = alpha;
          if (u.role) drawRoleMark(g, u.role, p.x, p.y, ROLE_BADGE);   // 201 (8): the figure is 7–25 px here, so the mark carries the role
          else {
            g.beginPath(); g.arc(p.x, p.y, UNIT_DOT, 0, Math.PI * 2);
            g.fillStyle = color; g.fill();
            g.lineWidth = 1; g.strokeStyle = '#0e1018'; g.stroke();
          }
          g.restore();
        }
      }
      // the team's squads: a path to the order's point, the block, the order under it. 68.35 (the owner's pick, A): blocks that
      // would land on one another merge into one — how many squads, their total standing, their letters — and a click on it
      // selects them (selected, they keep their own blocks: expanded); a group or a lone block still on another is stacked
      // downward. 68.36: a split squad's other clusters never merge; they hang off wherever their squad is drawn.
      const shown = table.squads.filter(m => m.centre && m.aboard === undefined);   // (68.30: a squad wholly aboard is a tab on its vehicle's plate)
      const at0 = new Map<number, { x: number; y: number }>();
      for (const m of shown) { const p0 = this.project(state, camera, m.planetIndex, m.centre!, lift); if (p0) at0.set(m.id, p0); }
      this.groupOf.clear();
      for (const grp of mergeScreenBlocks(shown.filter(m => at0.has(m.id)).map(m => ({ id: m.id, ...at0.get(m.id)!, selected: selected.has(m.id) })))) {
        const p = { x: grp.x, y: grp.y };
        while (placed.some(q => Math.abs(q.x - p.x) < 60 && Math.abs(q.y - p.y) < 38)) p.y += 40;
        placed.push(p);
        const members = grp.ids.map(id => shown.find(m => m.id === id)!);
        for (const m of members) { this.drawnAt.set(m.id, p); this.groupOf.set(m.id, grp.ids); }
        const color = TEAMS[members[0].team % TEAMS.length].primary;
        for (const m of members) {
          if (members.length === 1 && m.target) {
            const t = this.project(state, camera, m.target.planetIndex, m.target.dir, lift);
            if (t) {
              g.save(); g.setLineDash([6, 5]); g.strokeStyle = color; g.lineWidth = 2; g.beginPath(); g.moveTo(p.x, p.y); g.lineTo(t.x, t.y); g.stroke(); g.restore();
              orderMark(g, t.x, t.y, m.glyph, color, alpha);   // 68.24: the order's mark, the same as the ground view's
            }
          }
          // 68.36: a split squad — its other clusters as small blocks with their counts, a thin line back to where it is drawn
          for (const c of m.clusters ?? []) {
            const q = this.project(state, camera, m.planetIndex, c.dir, lift);
            if (!q) continue;
            g.save();
            g.setLineDash([2, 3]); g.strokeStyle = color; g.globalAlpha = 0.7; g.lineWidth = 1.5;
            g.beginPath(); g.moveTo(p.x, p.y); g.lineTo(q.x, q.y); g.stroke();
            g.setLineDash([]); g.globalAlpha = 1;
            g.fillStyle = color; g.strokeStyle = selected.has(m.id) ? '#ffffff' : 'rgba(0,0,0,0.6)'; g.lineWidth = selected.has(m.id) ? 2 : 1;
            g.beginPath(); g.rect(q.x - 9, q.y - 7, 18, 14); g.fill(); g.stroke();
            g.fillStyle = '#ffffff'; g.font = 'bold 10px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
            g.fillText(String(c.n), q.x, q.y + 0.5);
            g.restore();
          }
        }
        if (members.length === 1) this.block(g, p.x, p.y, members[0], color, selected.has(members[0].id));
        else this.mergedBlock(g, p.x, p.y, members, color);
      }
    }
    // 68.28 (the owner: "when in command view some indicator of the position of the guy you're controlling"): the unit you
    // hold — a ring in your colour with a tick the way it faces and "you", above the squads, never faded; none in free view
    const me = held === null ? null : state.players.find(p => p.id === held && p.alive);
    if (me) {
      const p = this.project(state, camera, me.planetIndex, me.surfaceDir, 0);
      if (p) {
        const col = TEAMS[me.teamIndex % TEAMS.length].primary;
        let tip: { x: number; y: number } | null = null;
        if (me.heading) {
          const ahead = vecNormalize(vecAdd(vecNormalize(me.surfaceDir), vecScale(me.heading, 1.5 * personSetOf(state).k * 1.8 / state.planets[me.planetIndex].radius)));
          tip = this.project(state, camera, me.planetIndex, ahead, 0);
        }
        g.save(); g.globalAlpha = 1;
        g.lineWidth = 4; g.strokeStyle = '#0e1018'; g.beginPath(); g.arc(p.x, p.y, YOU_RING, 0, Math.PI * 2); g.stroke();
        g.lineWidth = 2.2; g.strokeStyle = col; g.beginPath(); g.arc(p.x, p.y, YOU_RING, 0, Math.PI * 2); g.stroke();
        if (tip) {
          const dx = tip.x - p.x, dy = tip.y - p.y, l = Math.hypot(dx, dy) || 1;
          g.beginPath(); g.moveTo(p.x + (dx / l) * YOU_RING, p.y + (dy / l) * YOU_RING); g.lineTo(p.x + (dx / l) * (YOU_RING + 7), p.y + (dy / l) * (YOU_RING + 7)); g.stroke();
        }
        // "you" beside the ring, clear of its squad's block and order line (the held unit is one of its members)
        g.font = '700 10px system-ui, sans-serif'; g.textAlign = 'right'; g.textBaseline = 'bottom'; g.lineWidth = 3; g.strokeStyle = '#0e1018';
        const lx = p.x - YOU_RING - 2, ly = p.y - YOU_RING + 2;   // up and to the left: the squad's block and its order line sit at the ring
        g.strokeText('you', lx, ly); g.fillStyle = '#ffffff'; g.fillText('you', lx, ly);
        g.restore();
      }
    }
    g.globalAlpha = 1;
    // 68.14: the spot the next order will name — a ring on the ground under the cursor (snapped to a prop: its name)
    if (hover && !drag) {
      const p = this.project(state, camera, hover.planetIndex, hover.dir, 0);
      if (p) {
        g.save(); g.globalAlpha = alpha; g.strokeStyle = '#ffffff'; g.lineWidth = 1.5;
        g.beginPath(); g.ellipse(p.x, p.y, 11, 6, 0, 0, Math.PI * 2); g.stroke();
        g.fillStyle = '#ffffff'; g.beginPath(); g.arc(p.x, p.y, 1.8, 0, Math.PI * 2); g.fill();
        if (hover.label) { g.font = '500 10px system-ui, sans-serif'; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillStyle = 'rgba(12,12,24,0.75)'; const w = g.measureText(hover.label).width; g.fillRect(p.x + 14, p.y - 8, w + 8, 16); g.fillStyle = '#e8ecf4'; g.fillText(hover.label, p.x + 18, p.y); }
        g.restore();
      }
    }
    // 68.4: the team's reserve (X19 on) — what is still to come, who waits to come in, held or not, where it lands
    if (table.reserve) {
      const r = table.reserve;
      const text = `RESERVE  ${r.left} to come · ${r.waiting} waiting${r.held ? ' · HELD' : ''} · lands at ${r.landsAt === 'lane' ? 'the lane' : 'the pad'}   (F ▸ Mark: call it here / hold it)`;
      g.save(); g.globalAlpha = alpha; g.font = '600 11px system-ui, sans-serif'; g.textAlign = 'left'; g.textBaseline = 'middle';
      const w = g.measureText(text).width, x = 16, y = window.innerHeight - 40;
      g.fillStyle = 'rgba(12,12,24,0.8)'; g.beginPath(); g.roundRect(x, y - 13, w + 20, 26, 6); g.fill();
      g.strokeStyle = r.held ? '#e8b04a' : 'rgba(255,255,255,0.18)'; g.lineWidth = 1; g.stroke();
      g.fillStyle = r.held ? '#f3d18b' : '#e8ecf4'; g.fillText(text, x + 10, y);
      g.restore();
    }
    // 68.4: the box being dragged
    if (drag) {
      g.save(); g.strokeStyle = '#ffffff'; g.setLineDash([4, 3]); g.lineWidth = 1; g.fillStyle = 'rgba(255,255,255,0.06)';
      const x = Math.min(drag.x0, drag.x1), y = Math.min(drag.y0, drag.y1), bw = Math.abs(drag.x1 - drag.x0), bh = Math.abs(drag.y1 - drag.y0);
      g.fillRect(x, y, bw, bh); g.strokeRect(x, y, bw, bh); g.restore();
    }
    // the legend, and the card for the squad clicked
    this.legend.style.display = '';
    this.legend.textContent = note || `COMMAND VIEW · ${table.squads.filter(s => s.alive).length} squads · click or drag to select (1–9, 0 all) · hold F for orders, tap F for the obvious one · wheel: closer / farther · WASD or right-drag: move · Q / E: turn · C: back · O to return${this.radarHeads.length ? ' · R: radar detail' : ''}${table.pictureOn ? '' : ' · the team shares nothing it sees (Lab: Team picture)'}${table.onFoot ? ' · THE SIDE ON FOOT (Hold ▸ Vehicles free)' : ''}${table.respawn ? ` · respawn: ${table.respawn.kind === 'base' ? 'the base' : `${table.respawn.kind} ${table.respawn.structureId}`}${table.respawn.open ? '' : ' (cut — by the rule meanwhile)'}` : ' · Mark ▸ Respawn here to pick where you come back'}`;
    const chosen = table.squads.filter(s => selected.has(s.id));
    // 68.33: a place clicked (no squad chosen) — its card: what it is, how it stands, what it holds, and Respawn here
    if (!chosen.length && place) {
      const team = table.squads[0]?.team ?? 0, c = placeCard(state, team, place);
      // 166: a supply truck's card (no respawn there): its cargo and its leg
      const truck = c || place === 'base' ? null : truckCard(state, team, place.structureId);
      if (truck) {
        const html = truckCardHtml(truck);
        this.card.style.display = '';
        if (html !== this.cardHtml) { this.cardHtml = html; this.cardSupply = ''; this.card.innerHTML = html; }
        return;
      }
      if (c) {
        const stand = c.status === 'linked' ? '<span style="color:#6fd08c;">linked</span>' : c.status === 'cut' ? '<span style="color:#e8b04a;">cut from the web</span>' : '<span style="color:#e0864a;">down</span>';
        const holds = c.drops !== undefined ? `<div style="color:#c8ccd8;">drops left: <b>${c.drops}</b></div>` : c.crate ? `<div style="color:#c8ccd8;">crate: <b>${c.crate.bandages}</b> bandages · <b>${c.crate.magazines}</b> magazines</div>` : '';
        const button = c.picked
          ? `<div style="margin-top:8px; color:${c.open ? '#bdf0cc' : '#f3d18b'};">your respawn${c.open ? '' : ' — cut: by the rule meanwhile'}</div>`
          : `<button data-respawn style="margin-top:8px; padding:4px 10px; border-radius:6px; border:1px solid rgba(255,255,255,0.3); background:rgba(110,158,194,0.35); color:#fff; cursor:pointer;"${c.open ? '' : ' title="cut or down: the rule until it stands again"'}>Respawn here</button>`;
        // 166: the base's stock, trucks and runs; a FOB's fill, the run bound for it and what refills it (convoys on)
        const supply = c.kind === 'base' ? baseSupplyHtml(baseSupplyCard(state, team, state.tick ?? 0)) : c.kind === 'fob' && c.structureId !== undefined ? fobSupplyHtml(fobSupplyCard(state, team, c.structureId)) : '';
        const html = `<div style="font-size:14px; margin-bottom:2px;">${c.label}</div><div style="margin-bottom:4px;">${stand}</div>${holds}<div data-supply></div>${button}`;
        this.card.style.display = '';
        // (set only when it changes: a button replaced every frame loses the click between its press and its release)
        if (html !== this.cardHtml) {
          this.cardHtml = html; this.cardSupply = '';
          this.card.innerHTML = html;
          this.card.querySelector('button[data-respawn]')?.addEventListener('click', ev => { ev.stopPropagation(); this.onRespawnHere?.(place); });
        }
        // (the supply lines by themselves: the countdown changes each second, and must not replace the button under the cursor)
        if (supply !== this.cardSupply) { this.cardSupply = supply; const el = this.card.querySelector<HTMLElement>('[data-supply]'); if (el) el.innerHTML = supply; }
        return;
      }
    }
    this.cardHtml = ''; this.cardSupply = '';
    if (chosen.length > 1) {
      this.card.style.display = '';
      this.card.innerHTML = `<div style="font-size:14px; margin-bottom:4px;">${chosen.length} squads</div>` + chosen.map(s => `<div style="color:#c8ccd8;">${s.label}: ${s.glyph} ${s.order} · ${s.alive}/${s.total}</div>`).join('');
      return;
    }
    const sel = chosen[0];
    if (!sel) { this.card.style.display = 'none'; return; }
    this.card.style.display = '';
    const color = TEAMS[sel.team % TEAMS.length].primary;
    this.card.innerHTML = `<div style="font-size:14px; color:${color}; margin-bottom:2px;">${sel.label}</div>`
      + `<div style="color:#c8ccd8; margin-bottom:6px;">order: <b>${sel.glyph} ${sel.order}</b> · ${sel.alive}/${sel.total} standing</div>`
      + table.plays.filter(p => p.squads.includes(sel.id) && p.phase !== 'done').map(p => playLineHtml({ id: p.id, ...(p.at ? { at: p.at } : {}), role: p.roles[sel.id], phase: p.phase as 'setting' | 'assault', lifted: p.lifted })).join('')   // 68.22: lane E's line
      + sel.members.map(u => `<div style="display:flex; gap:8px; align-items:center; margin:2px 0;${u.alive ? '' : ' opacity:0.5;'}"><span style="flex:1;">${u.name}${u.own ? ` <span style="color:#ffd27a;" title="its own order (68.5)">${u.own.glyph} ${u.own.text}</span>` : ''}</span><span style="color:#9aa0b4;">${u.doing}</span><span style="width:44px; height:4px; background:rgba(255,255,255,0.12); border-radius:2px; overflow:hidden;"><i style="display:block; height:100%; width:${Math.max(0, Math.min(100, u.health))}%; background:${u.health > 50 ? '#6fd08c' : u.health > 25 ? '#e0b341' : '#e0864a'};"></i></span></div>`).join('');
  }

  /** X40: each squad's word as last watched and when it changed — the bark's clock (core/squadIntent.ts observeIntent). */
  private readonly intentSeen = new Map<number, { intent: SquadIntent; at: number }>();
  /** X40: seconds since the squad's word changed (−1: not since it was first seen — no bark). */
  private intentAge(m: SquadMarker): number { return m.intent ? observeIntent(this.intentSeen, m.id, m.intent.intent, performance.now() / 1000, m.intent.age) : -1; }

  /** 68.35: a merged block — a larger block with the squads' total standing, how many squads, their letters (a click selects them). */
  private mergedBlock(g: CanvasRenderingContext2D, x: number, y: number, ms: readonly SquadMarker[], color: string): void {
    const bw = 36, bh = 24, alive = ms.reduce((a, m) => a + m.alive, 0), total = ms.reduce((a, m) => a + m.total, 0);
    g.save();
    g.fillStyle = color; g.strokeStyle = 'rgba(0,0,0,0.6)'; g.lineWidth = 1.5;
    g.beginPath(); g.rect(x - bw / 2 + 3, y - bh / 2 - 3, bw, bh); g.fill(); g.stroke();   // (a second block behind: more than one)
    g.beginPath(); g.rect(x - bw / 2, y - bh / 2, bw, bh); g.fill(); g.stroke();
    g.strokeStyle = 'rgba(255,255,255,0.45)'; g.lineWidth = 1;
    g.beginPath(); g.moveTo(x - bw / 2, y - bh / 2); g.lineTo(x + bw / 2, y + bh / 2); g.moveTo(x + bw / 2, y - bh / 2); g.lineTo(x - bw / 2, y + bh / 2); g.stroke();
    g.fillStyle = '#ffffff'; g.font = 'bold 11px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(`${alive}/${total}`, x, y + 0.5);
    g.textBaseline = 'top'; g.font = '11px system-ui, sans-serif'; g.fillStyle = '#eef1f6';
    g.fillText(`${ms.length} squads`, x, y + bh / 2 + 2);
    g.fillStyle = '#c8ccd8'; g.font = '10px system-ui, sans-serif';
    g.fillText(ms.map(m => m.label.split(' ')[0]).join('·'), x, y + bh / 2 + 15);
    g.restore();
  }

  /** A squad's block: a team-coloured rectangle with how many stand; the order under it. */
  private block(g: CanvasRenderingContext2D, x: number, y: number, m: SquadMarker, color: string, selected: boolean): void {
    const bw = 30, bh = 20;
    g.save();
    g.fillStyle = color;
    g.strokeStyle = selected ? '#ffffff' : 'rgba(0,0,0,0.6)';
    g.lineWidth = selected ? 2.5 : 1.5;
    g.beginPath(); g.rect(x - bw / 2, y - bh / 2, bw, bh); g.fill(); g.stroke();
    // X23: a unit symbol's cross for infantry
    g.strokeStyle = 'rgba(255,255,255,0.45)'; g.lineWidth = 1;
    g.beginPath(); g.moveTo(x - bw / 2, y - bh / 2); g.lineTo(x + bw / 2, y + bh / 2); g.moveTo(x + bw / 2, y - bh / 2); g.lineTo(x - bw / 2, y + bh / 2); g.stroke();
    g.fillStyle = '#ffffff'; g.font = '700 12px system-ui, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(String(m.alive), x, y + 1);
    this.orderLine(g, x, y + bh / 2 + 3, m);
    // X40: what the squad is doing now, beside its block — its glyph, its word quiet, bright while it barks (lane E's tag)
    if (m.intent) drawIntentTag(g, m.intent.intent, x + bw / 2 + 4, y - 8, color, { where: 'table', sinceChange: this.intentAge(m) });
    g.restore();
  }

  /** Under a squad's block (or 68.30's plate): the squad's letter, its order's icon (lane E's set) and the order in words. */
  private orderLine(g: CanvasRenderingContext2D, x: number, top: number, m: SquadMarker): void {
    g.save();
    g.font = '500 11px system-ui, sans-serif'; g.textBaseline = 'top';
    const head = `${m.label.split(' ')[0]} `, words = ` ${m.order}`;
    const hw = g.measureText(head).width, ww = g.measureText(words).width, iw = 12;
    const tw = hw + iw + ww, left = x - tw / 2;
    g.fillStyle = 'rgba(12,12,24,0.75)'; g.fillRect(left - 4, top, tw + 8, 15);
    g.fillStyle = '#e8eaf0'; g.textAlign = 'left';
    g.fillText(head, left, top + 2);
    drawIcon(g, orderIconOf(m.glyph), left + hw, top + 1.5, iw, '#e8eaf0');
    g.fillText(words, left + hw + iw, top + 2);
    g.restore();
  }

  private marker(kind: TrackMarkerKind, confidence: number): HTMLCanvasElement {
    const step = Math.round(confidence * 10);
    const key = `${kind}-${step}`;
    let c = this.markerCache.get(key);
    if (!c) {
      c = document.createElement('canvas'); c.width = c.height = 56;
      drawTrackMarker(c.getContext('2d')!, 56, kind, step / 10, ENEMY);
      this.markerCache.set(key, c);
    }
    return c;
  }
}

/** 166: a truck's leg in a few words (the table's label and its card). */
function truckLegWords(c: TruckCard): string {
  return c.leg === 'out' ? `to FOB ${c.fobId}` : c.leg === 'back' ? `home from FOB ${c.fobId}` : c.leg === 'home' ? 'at the base' : 'parked';
}

const line = (html: string) => `<div style="color:#c8ccd8;">${html}</div>`;

/** 166: the base card's supply lines — its stock of loads, the next one, its trucks and the runs under way (none: convoys off). */
function baseSupplyHtml(b: ReturnType<typeof baseSupplyCard>): string {
  if (!b) return '';
  const low = b.stock <= b.minimum;
  const next = b.nextS === null ? 'full' : `next in ${Math.floor(b.nextS / 60)}:${String(b.nextS % 60).padStart(2, '0')}`;
  return line(`stock: <b style="color:${low ? '#e8b04a' : 'inherit'};">${b.stock}</b> of ${b.max} loads · keeps ${b.minimum} · ${next}`)
    + b.trucks.map(t => line(`truck ${t.truckId}: ${t.full ? 'loaded' : t.cargo.bandages + t.cargo.magazines > 0 ? 'part loaded' : 'empty'} · ${truckLegWords(t)}${t.driver ? ` (${t.driver})` : ''}`)).join('')
    + (b.trucks.length ? '' : line('<span style="color:#e8b04a;">no supply truck standing</span>'))
    + line(`runs under way: <b>${b.runs}</b>`);
}

/** 166: a FOB card's supply lines — its crate's fill, the run bound for it, and what refills it (none: convoys off). */
function fobSupplyHtml(f: ReturnType<typeof fobSupplyCard>): string {
  if (!f) return '';
  const pct = Math.round(f.fill * 100);
  return line(`fill: <b style="color:${f.fill < 0.5 ? '#e8b04a' : 'inherit'};">${pct}%</b> · refilled by ${f.by === 'runs' ? 'supply runs' : 'the link'}`)
    + line(f.run ? `run on its way: truck ${f.run.truckId}${f.run.driver ? ` (${f.run.driver})` : ''}` : 'no run on its way');
}

/** 166: a supply truck's card — its cargo of a full load, its leg and driver. */
function truckCardHtml(t: TruckCard): string {
  if (t.down) return `<div style="font-size:14px; margin-bottom:2px;">${t.label}</div>` + line('<span style="color:#e0864a;">destroyed</span> — its cargo lost with it');
  return `<div style="font-size:14px; margin-bottom:2px;">${t.label}</div>`
    + line(`cargo: <b>${t.cargo.bandages}</b> bandages · <b>${t.cargo.magazines}</b> magazines${t.full ? ' (a full load)' : ''}`)
    + line(`${truckLegWords(t)}${t.driver ? ` · driven by ${t.driver}` : ''}`);
}
